import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { participantViewService, type AssignedProject } from '@/services/participantViewService'
import RespondentShell from '@/components/respondent/RespondentShell'

type StudyState = 'todo' | 'inProgress' | 'done'

interface StudyItem extends AssignedProject {
  questionnaire_started?: boolean
  state: StudyState
}

export default function ParticipantDashboard() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('participant')
  const { user } = useAuth()
  const navigate = useNavigate()

  const [studies, setStudies] = useState<StudyItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setError(null)
        const projects = await participantViewService.getMyProjects()
        const withStatus = await Promise.all(
          projects.map(async (project) => {
            try {
              const detail = await participantViewService.getProjectDetail(project.id)
              return toStudyItem(detail, detail.questionnaire_completed, detail.questionnaire_started)
            } catch {
              return toStudyItem(project, false, false)
            }
          }),
        )
        if (!cancelled) setStudies(withStatus)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('loadFailed'))
          setStudies([])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [t])

  const greetingName = user?.first_name || user?.email?.split('@')[0] || ''
  const greetingKey = useMemo(() => {
    const h = new Date().getHours()
    return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening'
  }, [])

  const loading = studies === null
  const todo = useMemo(() => (studies ?? []).filter((s) => s.state !== 'done'), [studies])
  const done = useMemo(() => (studies ?? []).filter((s) => s.state === 'done'), [studies])

  /** Start (or resume) the full capture journey, attributed to this user. The
   *  join call creates a tracked participant row for a public study on first
   *  start, then hands back the tokens to launch the public journey. */
  const launchStudy = async (study: StudyItem) => {
    try {
      const { share_token, invite_token } = await participantViewService.joinStudy(study.id)
      const q = invite_token ? `?invite=${encodeURIComponent(invite_token)}` : ''
      navigate(`/r/${share_token}${q}`)
    } catch {
      navigate(`/participant/projects/${study.id}`)
    }
  }

  return (
    <RespondentShell>
      <Stack spacing={4} sx={{ maxWidth: 980, mx: 'auto', width: '100%', px: { xs: 2, md: 0 } }}>
        {/* Greeting + summary */}
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <Typography variant="h1" sx={{ fontSize: { xs: 24, md: 30 } }}>
              {t(`greeting.${greetingKey}`, { name: greetingName })}
            </Typography>
            <Typography sx={{ fontSize: 15, color: c.ink56 }}>{t('respondentDashboardSubtitle')}</Typography>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.75 }}>
            <SummaryStat label={t('summaryAssigned')} value={loading ? '—' : String((studies ?? []).length)} tone="neutral" />
            <SummaryStat label={t('summaryTodo')} value={loading ? '—' : String(todo.length)} tone="todo" />
            <SummaryStat label={t('summaryDone')} value={loading ? '—' : String(done.length)} tone="done" />
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* To do */}
            <Section title={t('sectionTodo')} subtitle={t('sectionTodoSub')} count={todo.length}>
              {todo.length === 0 ? (
                <EmptyState title={t('noTodo')} hint={t('noTodoHint')} />
              ) : (
                <Stack spacing={1.5}>
                  {todo.map((study) => (
                    <StudyCard
                      key={study.id}
                      study={study}
                      onOpen={() => navigate(`/participant/projects/${study.id}`)}
                      onLaunch={() => void launchStudy(study)}
                    />
                  ))}
                </Stack>
              )}
            </Section>

            {/* Completed */}
            <Section title={t('sectionDone')} subtitle={t('sectionDoneSub')} count={done.length}>
              {done.length === 0 ? (
                <EmptyState title={t('noDone')} hint={t('noDoneHint')} />
              ) : (
                <Stack spacing={1.5}>
                  {done.map((study) => (
                    <StudyCard
                      key={study.id}
                      study={study}
                      onOpen={() => navigate(`/participant/projects/${study.id}`)}
                      onLaunch={() => navigate(`/participant/projects/${study.id}`)}
                    />
                  ))}
                </Stack>
              )}
            </Section>
          </>
        )}
      </Stack>
    </RespondentShell>
  )
}

function toStudyItem(project: AssignedProject, completed?: boolean, started?: boolean): StudyItem {
  const inProgress = !completed && (started || project.participant_status === 'in_progress')
  const state: StudyState = completed ? 'done' : inProgress ? 'inProgress' : 'todo'
  return { ...project, questionnaire_completed: completed, questionnaire_started: started, state }
}

/** Light section header with a count chip. */
function Section({
  title,
  subtitle,
  count,
  children,
}: {
  title: string
  subtitle: string
  count: number
  children: React.ReactNode
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Stack spacing={1.75}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
        <Typography variant="h2" sx={{ fontSize: 19 }}>
          {title}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: 34,
            bgcolor: c.gray100,
            color: c.ink56,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </Box>
        <Typography sx={{ fontSize: 13, color: c.ink48, ml: 'auto' }}>{subtitle}</Typography>
      </Box>
      {children}
    </Stack>
  )
}

/** A single study row card, styled like the researcher surface. */
function StudyCard({ study, onOpen, onLaunch }: { study: StudyItem; onOpen: () => void; onLaunch: () => void }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('participant')

  const done = study.state === 'done'
  const inProgress = study.state === 'inProgress'
  const cta = done ? t('reviewStudy') : inProgress ? t('continueStudy') : t('startStudy')

  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: 2.25,
        borderRadius: `${theme.affexy.radii.md}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        cursor: 'pointer',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': { borderColor: alpha(c.coral, 0.5), boxShadow: theme.affexy.shadows.card },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: `${theme.affexy.radii.sm}px`,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: done ? alpha(c.success, 0.14) : alpha(c.coral, 0.1),
          color: done ? c.success : c.coral,
        }}
      >
        {done ? <CheckCircleOutlineIcon sx={{ fontSize: 22 }} /> : <FolderOutlinedIcon sx={{ fontSize: 22 }} />}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.ink }} noWrap>
            {study.name}
          </Typography>
          <StatePill state={study.state} />
        </Box>
        {study.description && (
          <Typography sx={{ fontSize: 13, color: c.ink56, mt: 0.25 }} noWrap>
            {study.description}
          </Typography>
        )}
      </Box>

      <Button
        variant={done ? 'outlined' : 'contained'}
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          onLaunch()
        }}
        startIcon={
          done ? <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
        }
        sx={{ flexShrink: 0, whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
      >
        {cta}
      </Button>
    </Paper>
  )
}

/** Status pill with respondent-facing wording. */
function StatePill({ state }: { state: StudyState }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('participant')

  const map: Record<StudyState, { bg: string; fg: string; dot: string; label: string }> = {
    todo: { bg: alpha(c.gray300, 0.28), fg: c.ink56, dot: c.gray300, label: t('stateTodo') },
    inProgress: { bg: alpha(c.warning, 0.16), fg: '#B0701F', dot: c.warning, label: t('stateInProgress') },
    done: { bg: alpha(c.success, 0.16), fg: '#3D8077', dot: c.success, label: t('stateDone') },
  }
  const s = map[state]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        pl: 1,
        pr: 1.25,
        py: 0.375,
        borderRadius: 34,
        fontSize: 11.5,
        fontWeight: 500,
        bgcolor: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.dot }} />
      {s.label}
    </Box>
  )
}

function SummaryStat({ label, value, tone }: { label: string; value: string; tone: 'neutral' | 'todo' | 'done' }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const accent = tone === 'done' ? c.success : tone === 'todo' ? c.coral : c.ink
  const Icon = tone === 'done' ? CheckCircleOutlineIcon : tone === 'todo' ? AssignmentOutlinedIcon : FolderOutlinedIcon
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: `${theme.affexy.radii.md}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ fontSize: 11, color: c.ink48, lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Icon sx={{ fontSize: 16, color: alpha(accent, 0.8) }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 28, lineHeight: 1, color: accent, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Paper>
  )
}

function EmptyState({ title, hint }: { title: string; hint: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Paper
      variant="outlined"
      sx={{
        py: 4,
        px: 3,
        borderRadius: `${theme.affexy.radii.md}px`,
        borderColor: 'divider',
        borderStyle: 'dashed',
        textAlign: 'center',
        bgcolor: c.white,
      }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 34, color: c.ink32, mb: 1 }} />
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.ink72 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: c.ink48, mt: 0.5 }}>{hint}</Typography>
    </Paper>
  )
}
